import { useEffect, useRef, useState, useCallback } from "react";
import loadYouTubeApi from "./loadYouTubeApi";

export function useYouTubePlayer(containerId, playlistId, onTrackTransition) {
  const playerRef = useRef(null);
  const [ready, setReady] = useState(false);
  const [playing, setPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [videoId, setVideoId] = useState(null);
  const [playlistIndex, setPlaylistIndex] = useState(0);
  const currentIndexRef = useRef(0);
  const onTransitionRef = useRef(onTrackTransition);
  onTransitionRef.current = onTrackTransition;

  const emitTransition = useCallback((completed) => {
    try {
      const idx = currentIndexRef.current;
      const d = playerRef.current.getDuration() || 0;
      const c = playerRef.current.getCurrentTime() || 0;
      const pct = d ? Math.min(100, Math.round((c / d) * 100)) : 0;
      if (onTransitionRef.current) onTransitionRef.current({ index: idx, completed, pct });
    } catch (e) {}
  }, []);

  useEffect(() => {
    let destroyed = false;
    let tickId = null;
    setReady(false);
    setPlaying(false);
    loadYouTubeApi().then(() => {
      if (destroyed) return;
      playerRef.current = new window.YT.Player(containerId, {
        width: "2",
        height: "2",
        playerVars: { listType: "playlist", list: playlistId, controls: 0, rel: 0, playsinline: 1, origin: window.location.origin },
        events: {
          onReady: () => {
            setReady(true);
            try { playerRef.current.setVolume(100); } catch (e) {}
          },
          onStateChange: (e) => {
            const isPlaying = e.data === window.YT.PlayerState.PLAYING;
            setPlaying(isPlaying);
            if (e.data === window.YT.PlayerState.ENDED) {
              emitTransition(true);
            }
            try {
              const vd = playerRef.current.getVideoData() || {};
              setVideoId(vd.video_id || null);
            } catch (err) {}
            try {
              const idx = Math.max(0, playerRef.current.getPlaylistIndex());
              currentIndexRef.current = idx;
              setPlaylistIndex(idx);
            } catch (err) {}
            if (isPlaying && !tickId) {
              tickId = setInterval(() => {
                try {
                  setCurrentTime(playerRef.current.getCurrentTime() || 0);
                  setDuration(playerRef.current.getDuration() || 0);
                } catch (err) {}
              }, 500);
            }
          },
          onError: () => {
            emitTransition(false);
            try { playerRef.current.nextVideo(); } catch (err) {}
          },
        },
      });
    });
    return () => {
      destroyed = true;
      if (tickId) clearInterval(tickId);
      try { playerRef.current && playerRef.current.destroy(); } catch (e) {}
      playerRef.current = null;
    };
  }, [containerId, playlistId, emitTransition]);

  const play = useCallback(() => { try { playerRef.current.playVideo(); } catch (e) {} }, []);
  const pause = useCallback(() => { try { playerRef.current.pauseVideo(); } catch (e) {} }, []);
  const next = useCallback(() => {
    emitTransition(false);
    try { playerRef.current.nextVideo(); } catch (e) {}
  }, [emitTransition]);
  const prev = useCallback(() => {
    emitTransition(false);
    try { playerRef.current.previousVideo(); } catch (e) {}
  }, [emitTransition]);
  const seekTo = useCallback((sec) => { try { playerRef.current.seekTo(sec, true); } catch (e) {} }, []);
  const setVolume = useCallback((v) => { try { playerRef.current.setVolume(v); } catch (e) {} }, []);

  return { ready, playing, currentTime, duration, videoId, playlistIndex, play, pause, next, prev, seekTo, setVolume };
}
